/* ============================================================================
   UI / layout — containers and page scaffolding.
   Card, CardButton, Section, Page, PageHeader, FormGrid, SideNav, SettingRow.
   ============================================================================ */
import React from "react";
import { cx } from "./util.js";

/* A framed block with an optional title, subtitle and actions; the default container for anything on a page. */
export function Card({ title, subtitle, actions, className, children, ...props }) {
  return (
    <section className={cx("ds-card", className)} {...props}>
      {(title || actions) && (
        <div className="ds-card__head">
          <div>
            {title && <h3 className="ds-card__title">{title}</h3>}
            {subtitle && <p className="ds-card__sub">{subtitle}</p>}
          </div>
          {actions && <div className="ds-toolbar">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* A whole card that is one button: it opens something, or picks one of a set (`selected` marks the picked one and is
   announced as pressed). Its words are its children. */
export function CardButton({ selected, className, children, ...props }) {
  return (
    <button type="button" className={cx("ds-card ds-card-hover ds-card-button", selected && "active", className)} aria-pressed={selected} {...props}>
      {children}
    </button>
  );
}

/* A titled block of a page with optional actions at its right end; a Card without the frame. */
export function Section({ title, actions, children }) {
  return (
    <section className="ds-section">
      {(title || actions) && (
        <div className="ds-section__head">
          {title && <h3 className="ds-h3">{title}</h3>}
          <span className="ds-section__spacer" />
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/* The outer wrapper of every screen: sets the page width, padding and the gap between its blocks. Put PageHeader first. */
export function Page({ children, className }) {
  return <div className={cx("ds-page", className)}>{children}</div>;
}

/* The heading of a screen: its title (the one h1), one line saying what the page is for, and the page-level actions at the right. The top bar already names the section, so there is no eyebrow. */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <header className="ds-page-header">
      <div>
        <h1 className="ds-page-header__title">{title}</h1>
        {subtitle && <p className="ds-page-header__sub">{subtitle}</p>}
      </div>
      {actions && <div className="ds-page-header__actions">{actions}</div>}
    </header>
  );
}

/* Fields laid out in a grid that stacks on a phone: columns is 1, 2 or 3. */
export function FormGrid({ columns = 2, children }) {
  return <div className={cx("ds-form-grid", `ds-form-grid--${columns}`)}>{children}</div>;
}

/* A vertical list of the sections of one screen, one of which is open. items: [{ key, label, icon, tone }]; `tone`
   "danger" paints an item that needs attention. The open one is marked aria-current. */
export function SideNav({ items, value, onChange, label }) {
  return (
    <nav className="ds-sidenav" aria-label={label}>
      {items.map((item) => (
        <button key={item.key} type="button" className={cx("ds-sidenav__item", item.tone && `is-${item.tone}`)}
          aria-current={value === item.key ? "page" : undefined} onClick={() => onChange(item.key)}>
          {item.icon}{item.label}
        </button>
      ))}
    </nav>
  );
}

/* One line of a list of settings or switches: what it is on the left (a label and a line of help), its control or
   status on the right. `indent` sets a child (an add-on under its product) in; `dim` is for one that is switched
   off by what it hangs from. */
export function SettingRow({ label, help, indent = false, dim = false, children }) {
  return (
    <div className={cx("ds-setting", indent && "is-indent", dim && "is-dim")}>
      <div className="ds-setting__main">
        <div className="ds-setting__label">{label}</div>
        {help && <div className="ds-card__sub">{help}</div>}
      </div>
      {children && <div className="ds-toolbar ds-setting__control">{children}</div>}
    </div>
  );
}
